import {
  PreScreeningQuestion,
  QuestionType,
  RadioGridQuickSelect,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { getMonthNameMonthsAgo } from "../../utils/dateUtils";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  GLP1_MEDICATIONS,
  DPP4_MEDICATIONS,
  CLINICAL_TRIAL_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  IMMUNOSUPPRESSANT_MEDICATIONS,
  CARDIOVASCULAR_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for new Type 2 Diabetes Management Study
 */
export const vikingT2dScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "diabetesType",
    type: QuestionType.RADIO,
    question: "Which type of diabetes do you have?",
    required: true,
    allowBack: true,
    options: [
      { value: "type2", label: "Type 2 diabetes" },
      { value: "type1", label: "Type 1 diabetes", disqualify: true },
      {
        value: "none",
        label: "I don't have diabetes",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study is only for people with Type 2 Diabetes.",
    attributeMapping: mappers.value({
      type2: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_PREDIABETES, booleanAttributeValue: true },
      ],
      type1: [{ attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: true }],
      none: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE1, booleanAttributeValue: false },
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "hba1cLevel",
    type: QuestionType.RADIO,
    question: "What was your most recent HbA1c level, if measured in the past 2 months?",
    helpText:
      "HbA1c is a blood test that measures your average blood sugar level over the past 3 months",
    required: true,
    allowBack: true,
    options: [
      {
        value: "less_than_6.5",
        label: "HbA1c less than 6.5%",
        disqualify: true,
      },
      { value: "6.5_to_11.5", label: "HbA1c 6.5% - 11.5%" },
      {
        value: "more_than_11.5",
        label: "HbA1c more than 11.5%",
        disqualify: true,
      },
      { value: "more_than_2mo", label: "Test was more than 2 months ago" },
      { value: "not_sure", label: "Not sure" },
    ],
    disqualifyMessage:
      "This study requires participants to have an HbA1c level between 6.5% and 11.5%.",
    attributeMapping: mappers.value({
      "less_than_6.5": [], // No specific attribute - eligibility criteria only
      "6.5_to_11.5": [{ attributeDefinitionId: 26559, booleanAttributeValue: true }], // has_a1c_6.5_and_11_binary
      "more_than_11.5": [], // No specific attribute - eligibility criteria only
      more_than_2mo: [], // No specific attribute for old test
      not_sure: [], // No specific attribute for unsure
    }),
  },
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    minBMI: 25,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },
  {
    id: "glp1History",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these medications?",
    helpText: "GLP-1 receptor agonists, given as injections or tablets ",
    required: true,
    quickSelect: RadioGridQuickSelect.createMedicationQuickSelect(),
    gridLayout: {
      columnHeaders: ["Currently taking",  "Never taken", `Stopped during or after ${getMonthNameMonthsAgo(6, true)}`, `Stopped during or before ${getMonthNameMonthsAgo(7, true)}`],
      rows: [
        {
          label: "Ozempic, Wegovy, or Rybelsus",
          sublabel: "Semaglutide",
          values: [
            { value: "semaglutide_current", disqualify: true },
            { value: "semaglutide_never" },
            { value: "semaglutide_stopped_after_jul_2025", disqualify: true },
            { value: "semaglutide_stopped_before_jun_2025" },
          ],
        },
        {
          label: "Mounjaro or Zepbound",
          sublabel: "Tirzepatide",
          values: [
            { value: "tirzepatide_current", disqualify: true },
            { value: "tirzepatide_never" },
            { value: "tirzepatide_stopped_after_jul_2025", disqualify: true },
            { value: "tirzepatide_stopped_before_jun_2025" },
          ],
        },
        {
          label: "Victoza or Saxenda",
          sublabel: "Liraglutide",
          values: [
            { value: "liraglutide_current", disqualify: true },
            { value: "liraglutide_never" },
            { value: "liraglutide_stopped_after_jul_2025", disqualify: true },
            { value: "liraglutide_stopped_before_jun_2025" },
          ],
        },
        {
          label: "Trulicity",
          sublabel: "Dulaglutide",
          values: [
            { value: "dulaglutide_current", disqualify: true },
            { value: "dulaglutide_never" },
            { value: "dulaglutide_stopped_after_jul_2025", disqualify: true },
            { value: "dulaglutide_stopped_before_jun_2025" },
          ],
        },
        {
          label: "Byetta or Bydureon",
          sublabel: "Exenatide",
          values: [
            { value: "exenatide_current", disqualify: true },
            { value: "exenatide_never" },
            { value: "exenatide_stopped_after_jul_2025", disqualify: true },
            { value: "exenatide_stopped_before_jun_2025" },
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study cannot include people who have taken GLP-1 medications in the past 6 months.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        semaglutide: GLP1_MEDICATIONS.SEMAGLUTIDE,
        tirzepatide: GLP1_MEDICATIONS.TIRZEPATIDE,
        liraglutide: GLP1_MEDICATIONS.LIRAGLUTIDE,
        dulaglutide: GLP1_MEDICATIONS.DULAGLUTIDE,
        exenatide: GLP1_MEDICATIONS.EXENATIDE,
      },
    }),
  },
  {
    id: "dpp4History",
    type: QuestionType.RADIO_GRID,
    question: "Do you take any of these medications?",
    helpText: "DPP-4 inhibitors",
    required: true,
    quickSelect: RadioGridQuickSelect.createMedicationQuickSelect(),
    gridLayout: {
      columnHeaders: ["Currently taking", "Not taking"],
      rows: [
        {
          label: "Januvia",
          sublabel: "Sitagliptin",
          values: [
            { value: "sitagliptin_current", disqualify: true },
            { value: "sitagliptin_not_taking" },
          ],
        },
        {
          label: "Kazano",
          sublabel: "Alogliptin with Metformin",
          values: [
            { value: "alogliptin_metformin_current", disqualify: true },
            { value: "alogliptin_metformin_not_taking" },
          ],
        },
        {
          label: "Janumet",
          sublabel: "Sitagliptin with Metformin",
          values: [
            { value: "metformin_sitagliptin_current", disqualify: true },
            { value: "metformin_sitagliptin_not_taking" },
          ],
        },
        {
          label: "Trijardy",
          sublabel: "Ampagliflozin/Linagliptin/Metformin",
          values: [
            { value: "empagliflozin_linagliptin_metformin_current", disqualify: true },
            { value: "empagliflozin_linagliptin_metformin_not_taking" },
          ],
        },
        {
          label: "Kombiglyze",
          sublabel: "Metformin with Saxagliptin",
          values: [
            { value: "metformin_saxagliptin_current", disqualify: true },
            { value: "metformin_saxagliptin_not_taking" },
          ],
        },
        {
          label: "Tradjenta",
          sublabel: "Linagliptin",
          values: [
            { value: "linagliptin_current", disqualify: true },
            { value: "linagliptin_not_taking" },
          ],
        },
        {
          label: "Juvisync",
          sublabel: "Simvastatin with Sitagliptin",
          values: [
            { value: "simvastatin_sitagliptin_current", disqualify: true },
            { value: "simvastatin_sitagliptin_not_taking" },
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study cannot include people who are currently taking DPP-4 medications.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        sitagliptin: DPP4_MEDICATIONS.SITAGLIPTIN,
        alogliptin_metformin: DPP4_MEDICATIONS.ALOGLIPTIN, // Note: response value is alogliptin_metformin, not just alogliptin
        metformin_sitagliptin: DPP4_MEDICATIONS.SITAGLIPTIN,
        empagliflozin_linagliptin_metformin: DPP4_MEDICATIONS.LINAGLIPTIN,
        metformin_saxagliptin: DPP4_MEDICATIONS.SAXAGLIPTIN,
        linagliptin: DPP4_MEDICATIONS.LINAGLIPTIN,
        simvastatin_sitagliptin: DPP4_MEDICATIONS.SITAGLIPTIN,
      },
    }),
  },
  {
    id: "insulinUse",
    type: QuestionType.RADIO,
    question: "Do you use insulin injections to manage your diabetes?",
    required: true,
    options: [
      { value: "regularly", label: "I use insulin injections regularly", disqualify: true },
      { value: "sometimes", label: "I use insulin injections sometimes", disqualify: true },
      { value: "no", label: "I don't use insulin injections" },
    ],
    disqualifyMessage:
      "This study cannot include people who are currently taking insulin.",
    attributeMapping: mappers.value({
      regularly: [{ attributeDefinitionId: 23574, booleanAttributeValue: true }], // has_insulin_daily
      sometimes: [{ attributeDefinitionId: 23574, booleanAttributeValue: true }], // has_insulin_daily
      no: [{ attributeDefinitionId: 23574, booleanAttributeValue: false }], // has_insulin_daily
    }),
  },
  {
    id: "stimulantOrWeightLossHistory",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these medications?",
    helpText: "Stimulants and weight loss medications",
    required: true,
    quickSelect: RadioGridQuickSelect.createMedicationQuickSelect(),
    gridLayout: {
      columnHeaders: ["Currently taking", "Never taken", `Stopped during or after ${getMonthNameMonthsAgo(3, true)}`, `Stopped during or before ${getMonthNameMonthsAgo(4, true)}`],
      rows: [
        {
          label: "Adderall",
          sublabel: "Amphetamine/Dextroamphetamine",
          values: [
            { value: "adderall_current", disqualify: true },
            { value: "adderall_never" },
            { value: "adderall_stopped_after_oct_2025", disqualify: true },
            { value: "adderall_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Vyvanse",
          sublabel: "Lisdexamfetamine",
          values: [
            { value: "vyvanse_current", disqualify: true },
            { value: "vyvanse_never" },
            { value: "vyvanse_stopped_after_oct_2025", disqualify: true },
            { value: "vyvanse_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Ritalin or Concerta",
          sublabel: "Methylphenidate",
          values: [
            { value: "ritalin_current", disqualify: true },
            { value: "ritalin_never" },
            { value: "ritalin_stopped_after_oct_2025", disqualify: true },
            { value: "ritalin_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Phentermine",
          sublabel: "Adipex-P, Lomaira",
          values: [
            { value: "phentermine_current", disqualify: true },
            { value: "phentermine_never" },
            { value: "phentermine_stopped_after_oct_2025", disqualify: true },
            { value: "phentermine_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Orlistat",
          sublabel: "Xenical, Alli",
          values: [
            { value: "orlistat_current", disqualify: true },
            { value: "orlistat_never" },
            { value: "orlistat_stopped_after_oct_2025", disqualify: true },
            { value: "orlistat_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Contrave",
          sublabel: "Naltrexone/Bupropion",
          values: [
            { value: "contrave_current", disqualify: true },
            { value: "contrave_never" },
            { value: "contrave_stopped_after_oct_2025", disqualify: true },
            { value: "contrave_stopped_before_sep_2025" },
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study cannot include people who have taken these stimulant or weight loss medications in the past 3 months.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        // Stimulants and weight loss medications
        adderall: 22921, // amphetamine (generic for Adderall)
        vyvanse: 21522, // lisdexamfetamine
        ritalin: 21450, // methylphenidate
        phentermine: 21509, // phentermine
        orlistat: 21508, // orlistat
        contrave: 23393, // naltrexone_bupropion
      },
    }),
  },
  {
    id: "corticosteroidHistory",
    type: QuestionType.RADIO_GRID,
    question:
      "Have you taken any of these medications regularly, in tablet or injection form?",
    helpText:
      "Corticosteroids are often used for autoimmune or inflammatory conditions",
    required: true,
    quickSelect: RadioGridQuickSelect.createMedicationQuickSelect(),
    gridLayout: {
      columnHeaders: ["Currently taking", "Never taken", `Stopped during or after ${getMonthNameMonthsAgo(3, true)}`, `Stopped during or before ${getMonthNameMonthsAgo(4, true)}`],
      rows: [
        {
          label: "Prednisone",
          sublabel: "Deltasone, Rayos",
          values: [
            { value: "prednisone_current", disqualify: true },
            { value: "prednisone_never" },
            { value: "prednisone_stopped_after_oct_2025", disqualify: true },
            { value: "prednisone_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Methylprednisolone",
          sublabel: "Medrol, Solu-Medrol",
          values: [
            { value: "methylprednisolone_current", disqualify: true },
            { value: "methylprednisolone_never" },
            { value: "methylprednisolone_stopped_after_oct_2025", disqualify: true },
            { value: "methylprednisolone_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Dexamethasone",
          sublabel: "Decadron, DexPak",
          values: [
            { value: "dexamethasone_current", disqualify: true },
            { value: "dexamethasone_never" },
            { value: "dexamethasone_stopped_after_oct_2025", disqualify: true },
            { value: "dexamethasone_stopped_before_sep_2025" },
          ],
        },
        {
          label: "Hydrocortisone",
          sublabel: "Cortef, Solu-Cortef",
          values: [
            { value: "hydrocortisone_current", disqualify: true },
            { value: "hydrocortisone_never" },
            { value: "hydrocortisone_stopped_after_oct_2025", disqualify: true },
            { value: "hydrocortisone_stopped_before_sep_2025" },
          ],
        },
      ],
    },
    disqualifyMessage:
      "This study cannot include people who have taken corticosteroids in the past 3 months.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        prednisone: IMMUNOSUPPRESSANT_MEDICATIONS.PREDNISONE,
        methylprednisolone: 21150, // methylprednisolone
        dexamethasone: 21151, // dexamethasone
        hydrocortisone: 21719, // hydrocortisone (oral/systemic)
      },
    }),
  },
  {
    id: "weightChange",
    type: QuestionType.RADIO,
    question: "How has your weight changed recently?",
    required: true,
    options: [
      {
        value: "current_weight",
        label: "My weight was around the same as now", // Fallback label
        dynamicLabel: (allAnswers) => {
          const weightStr = allAnswers?.["weight"] as string;
          const weight = parseFloat(weightStr);
          const month = getMonthNameMonthsAgo(3);
          if (isNaN(weight) || weight <= 0) {
            return "My weight was around the same as now";
          }
          return `In ${month}, my weight was around ${weight}lbs`;
        },
      },
      {
        value: "stable_weight",
        label: "My weight has been stable", // Fallback label
        dynamicLabel: (allAnswers) => {
          const weightStr = allAnswers?.["weight"] as string;
          const weight = parseFloat(weightStr);
          const month = getMonthNameMonthsAgo(3);
          if (isNaN(weight) || weight <= 0) {
            return "My weight has been stable";
          }
          const lowerBound = Math.floor(weight * 0.93);
          const upperBound = Math.ceil(weight * 1.07);
          return `In ${month}, my weight was between ${lowerBound}lbs and ${upperBound}lbs`;
        },
      },
      {
        value: "lost_7pc_weight",
        label: `Lost 7% or more since ${getMonthNameMonthsAgo(3)}`, // Fallback label
        dynamicLabel: (allAnswers) => {
          const weightStr = allAnswers?.["weight"] as string;
          const weight = parseFloat(weightStr);
          const month = getMonthNameMonthsAgo(3);
          if (isNaN(weight) || weight <= 0) {
            return `Lost 7% or more of your body weight since ${month}`;
          }
          // If they lost 7%, their past weight was higher than current weight / 0.93
          const pastWeight = Math.ceil(weight / 0.93);
          return `In ${month}, my weight was above ${pastWeight}lbs`;
        },
        disqualify: true,
      },
      {
        value: "gained_7pc_weight",
        label: `Gained 7% or more since ${getMonthNameMonthsAgo(3)}`, // Fallback label
        dynamicLabel: (allAnswers) => {
          const weightStr = allAnswers?.["weight"] as string;
          const weight = parseFloat(weightStr);
          const month = getMonthNameMonthsAgo(3);
          if (isNaN(weight) || weight <= 0) {
            return `Gained 7% or more of your body weight since ${month}`;
          }
          // If they gained 7%, their past weight was lower than current weight / 1.07
          const pastWeight = Math.floor(weight / 1.07);
          return `In ${month}, my weight was below ${pastWeight}lbs`;
        },
        disqualify: true,
      },
      {
        value: "unsure",
        label: "Not sure",
      },
    ],
    disqualifyMessage:
      "This study is not suitable for you if you have had significant weight changes recently.",
    attributeMapping: mappers.value({
      lost_7pc_weight: [], // No specific attribute for weight loss
      gained_7pc_weight: [], // No specific attribute for weight gain
      stable_weight: [], // No specific attribute for stable weight
      current_weight: [], // No specific attribute for current weight
      unsure: [], // No specific attribute for unsure
    }),
  },
  {
    id: "comorbidExclusions1",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you? (Part 1)",
    required: true,
    options: [
      {
        value: "weight_loss_surgery",
        label: "I have had weight loss surgery (such as gastric bypass, gastric sleeve, gastric banding or stomach stapling)",
        disqualify: true,
      },
      {
        value: "pancreatitis",
        label: "I have had pancreatitis in the past",
        disqualify: true,
      },
      {
        value: "active_cancer",
        label:
          "I have been treated for cancer in the past 5 years (excluding BCC, SCC, in-situ cervical cancer and in-situ prostate cancer) ",
        disqualify: true,
      },
      {
        value: "liver_failure",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "kidney_failure",
        label: "I have end stage kidney failure or am on dialysis",
        disqualify: true,
      },
      { value: "none", label: "None of the above" },
    ],
    disqualifyMessage:
      "This study is not suitable if you have any of these conditions.",
    attributeMapping: mappers.value({
      weight_loss_surgery: [{ attributeDefinitionId: 20918, booleanAttributeValue: true }], // had_bariatricsurgery
      pancreatitis: [{ attributeDefinitionId: 147, booleanAttributeValue: true }], // has_pancreatitis
      active_cancer: [{ attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true }],
      liver_failure: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true }],
      kidney_failure: [{ attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true }],
      none: [
        { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "comorbidExclusions2",
    type: QuestionType.RADIO,
    question: "Are any of these statements true for you? (Part 2)",
    required: true,
    options: [
      {
        value: "psychiatric_hospitalization",
        label:
          "I needed to be hospitalised for mental health treatment in the past 12 months",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had an organ transplant (not including corneal transplant)",
        disqualify: true,
      },
      {
        value: "sickle_cell",
        label: "I have sickle cell disease",
        disqualify: true,
      },
      {
        value: "heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "pregnant_breastfeeding",
        label: "I am currently pregnant or breastfeeding",
        disqualify: true,
      },
      { value: "none", label: "None of the above" },
    ],
    disqualifyMessage:
      "This study is not suitable if you have any of these conditions.",
    attributeMapping: mappers.value({
      psychiatric_hospitalization: [{ attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION_HOSPITALIZED_4_WEEKS, booleanAttributeValue: true }],
      organ_transplant: [{ attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true }],
      sickle_cell: [{ attributeDefinitionId: 25006, booleanAttributeValue: true }], // has_sickle_cell_disease
      heart_failure: [{ attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true }],
      pregnant_breastfeeding: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: true },
      ],
      none: [
        { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_DEPRESSION_HOSPITALIZED_4_WEEKS, booleanAttributeValue: false },
        { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_BREASTFEEDING, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "visitCommitment",
    type: QuestionType.RADIO,
    question:
      "Are you willing to attend in-person visits?",
    required: true,
    options: [
      {
        value: "yes",
        label: "Yes, I can attend weekly, then monthly visits for around 18 months in total",
      },
      {
        value: "no",
        label: "No, I cannot commit to this appointment schedule",
        disqualify: true,
      },
      {
        value: "discuss",
        label:
          "I would like to discuss the schedule further with the research team",
      },
    ],
    disqualifyMessage:
      "This study requires regular in-person visits for 18 months.",
    attributeMapping: mappers.value({
      yes: [], // No specific attribute for study visit availability
      discuss: [], // No specific attribute for study visit availability
      no: [], // No specific attribute for unavailability
    }),
  },
  {
    id: "currentTrial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial?",
    required: true,
    options: [
      {
        value: "yes",
        label: "Yes",
        disqualify: true,
      },
      { value: "no", label: "No" },
    ],
    disqualifyMessage: "You can only be in one clinical trial at a time.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false }],
    }),
  },
];
